import { useState, useEffect, useCallback, useMemo } from 'react';
import { ClientPerformanceData, ClientPerformanceFilters } from '../types';
import clientPerformanceApi from '../services/clientPerformanceApi';

interface UseClientPerformanceReturn {
  data: ClientPerformanceData[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  exportData: () => void;
  stats: {
    totalVolume: number;
    averageInboxRate: number;
    averageSpamRate: number;
    atRiskClients: number;
    totalClients: number;
  };
}

export const useClientPerformance = (
  filters?: ClientPerformanceFilters
): UseClientPerformanceReturn => {
  const [data, setData] = useState<ClientPerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const performanceData = await clientPerformanceApi.fetchPerformanceData(filters);
      setData(performanceData);
    } catch (err) {
      console.error('Error fetching client performance data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch performance data');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = useCallback(async () => {
    clientPerformanceApi.clearCache();
    await fetchData();
  }, [fetchData]);

  const exportData = useCallback(() => {
    if (data.length > 0) {
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `client-performance-${dateStr}.csv`;
      clientPerformanceApi.exportToCSV(data, filename);
    }
  }, [data]);

  const stats = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalVolume: 0,
        averageInboxRate: 0,
        averageSpamRate: 0,
        atRiskClients: 0,
        totalClients: 0
      };
    }

    let totalVolume = 0;
    let totalInboxRate = 0;
    let totalSpamRate = 0;
    let atRiskClients = 0;
    let validInboxCount = 0;
    let validSpamCount = 0;

    data.forEach(item => {
      // Calculate total volume
      const volume = typeof item.total_volume === 'number' 
        ? item.total_volume 
        : parseInt(String(item.total_volume));
      if (!isNaN(volume)) {
        totalVolume += volume;
      }

      // Calculate average inbox rate
      const inboxRate = parseFloat(item.overall_inbox_pct.replace('%', ''));
      if (!isNaN(inboxRate)) {
        totalInboxRate += inboxRate;
        validInboxCount++;
        
        // Count at-risk clients (inbox rate < 70%)
        if (inboxRate < 70) {
          atRiskClients++;
        }
      }

      // Calculate average spam rate
      const spamRate = parseFloat(item.spam_rate.replace('%', ''));
      if (!isNaN(spamRate)) {
        totalSpamRate += spamRate;
        validSpamCount++;
        
        // Count at-risk clients (spam rate > 5%)
        if (spamRate > 5) {
          atRiskClients++;
        }
      }
    });

    return {
      totalVolume,
      averageInboxRate: validInboxCount > 0 ? totalInboxRate / validInboxCount : 0,
      averageSpamRate: validSpamCount > 0 ? totalSpamRate / validSpamCount : 0,
      atRiskClients: Math.min(atRiskClients, data.length), // Ensure not double-counting
      totalClients: data.length
    };
  }, [data]);

  return {
    data,
    loading,
    error,
    refresh,
    exportData,
    stats
  };
};